import React, { useContext, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useQuery } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';

import Notes from 'app/src/components/Notes/List';
import ClientContext from 'app/src/contexts/ClientContext';
import { useParams } from 'app/src/utils/routing';
import DataState from 'app/src/elements/DataState';
import {
  Text,
  Title,
  TitleLinks,
  TitleWithLinks,
  Subtitle,
  Container,
  Additional,
} from 'app/src/styles';
import { datetimeShort } from 'app/src/utils/formatting';
import { Link } from 'app/src/utils/routing';

import CommissionDetails from './CommissionDetails';
import HideFromDealer from './HideFromDealer';
import PrintPreview from './PrintPreview';
import ShopifyItem from './ShopifyItem';
import { ORDER } from './queries';

const Order = () => {
  const { currentUser, hasPolicy, isMobile, isTablet } = useContext(ClientContext);
  const [showHideFromDealer, setShowHideFromDealer] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);

  if (!currentUser?.isEmployee) {
    return null;
  }

  const { id } = useParams();
  const { data, refetch, ...dataState } = useQuery(ORDER, {
    variables: { id }
  });

  return (
    <View>
      <DataState data={data} {...dataState}>
        <TitleWithLinks isMobile={isMobile}>
          <Title withLinks>
            Order
            {data?.order.filledAt && ` ${datetimeShort(data.order.filledAt)}`}
            {data?.order.hiddenFromDealerAt ? ' - NOT SHOWN TO DEALER' : ''}
          </Title>

          <TitleLinks>
            <Link to={`/orders/${id}/tags`}>
              <Text>⤇ View Tag Data</Text>
            </Link>

            <Pressable onPress={() => setShowPrintPreview(true)}>
              <Text>⤇ View Tags Print Preview</Text>
            </Pressable>

            {hasPolicy({ type: 'Order', action: 'hide_from_dealer' }) && (
              <Pressable onPress={() => setShowHideFromDealer(true)}>
                {data?.order.hiddenFromDealerAt ? (
                  <Text>⤇ Unhide From Dealer</Text>
                ) : (
                  <Text>⤇ Hide From Dealer</Text>
                )}
              </Pressable>
            )}
          </TitleLinks>
        </TitleWithLinks>

        {data && (
          <Container isTablet={isTablet}>
            <View style={{ flex: 1, zIndex: 500 }}>
              <View
                style={{
                  flexDirection: 'row', gap: 10, alignItems: 'baseline'
                }}
              >
                <Subtitle>{data.order.name}</Subtitle>
                <Link to={`/dealers/${data.order.dealer.id}`}>
                  <FontAwesomeIcon icon={faEye} color="green" size={24} />
                </Link>
              </View>

              <Text>{data.order.product.name}</Text>

              {data.order.newLicense && (
                <Text>{data.order.newLicense.licenseString}</Text>
              )}

              {data.order.usedLicense && (
                <Text>{data.order.usedLicense.licenseString}</Text>
              )}

              <Text>
                {data.order.quantity} Tags - {data.order.serialSummary}
              </Text>

              <Text>
                Shopify Order #: {data.order.shopifyOrderNumber}
              </Text>

              <Subtitle bordered>Order Notes</Subtitle>
              <Notes
                notes={data.order.notes}
                order={data.order}
                dealer={data.order.dealer}
                refetch={refetch}
              />

              <Subtitle bordered>Dealer Notes</Subtitle>
              <Notes
                notes={data.order.dealer.notes}
                dealer={data.order.dealer}
                refetch={refetch}
              />
            </View>

            <Additional isTablet={isTablet}>
              <CommissionDetails
                usedLicense={data.order.usedLicense}
                asOf={data.order.filledAt}
              />

              {data.order.shopifyItem && (
                <ShopifyItem item={data.order.shopifyItem} />
              )}

              <View>
                <Title>Printer Data</Title>

                {data.order.printScrapeNotes.map(note => (
                  <Link key={note.id} to={`/notes/${note.id}`}>
                    <Text>
                      {datetimeShort(note.createdAt)} - {note.type}
                    </Text>
                  </Link>
                ))}
              </View>
            </Additional>
          </Container>
        )}
      </DataState>

      {showHideFromDealer && data && (
        <HideFromDealer
          order={data.order}
          setShow={setShowHideFromDealer}
          refetch={refetch}
        />
      )}

      {showPrintPreview && (
        <PrintPreview order={data.order} setShow={setShowPrintPreview} />
      )}
    </View>
  );
};

export default Order;
